import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";

function renderFormattedText(text: string): React.ReactNode {
  // Split on **bold** first, then handle *italic* within each segment
  const parts: React.ReactNode[] = [];
  const boldRegex = /\*\*(.+?)\*\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = boldRegex.exec(text)) !== null) {
    // Add text before this bold match (may contain italics)
    if (match.index > lastIndex) {
      parts.push(
        ...renderItalic(text.slice(lastIndex, match.index), parts.length),
      );
    }
    // Add the bold element
    parts.push(
      <strong
        key={`b-${match.index}`}
        className="font-semibold text-slate-900 dark:text-white"
      >
        {match[1]}
      </strong>,
    );
    lastIndex = match.index + match[0].length;
  }

  // Add remaining text after last bold match
  if (lastIndex < text.length) {
    parts.push(...renderItalic(text.slice(lastIndex), parts.length));
  }

  return parts.length === 1 ? parts[0] : parts;
}

function renderItalic(text: string, keyOffset: number): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const italicRegex = /\*(.+?)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = italicRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(<em key={`i-${keyOffset}-${match.index}`}>{match[1]}</em>);
    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  if (parts.length === 0) {
    parts.push(text);
  }

  return parts;
}

const EFFECTIVE_DATE = "March 1, 2026";

const SECTIONS = [
  {
    title: "1. What Are Cookies",
    content: `Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently, provide information to site owners, and improve your browsing experience. Cookies can be "session" cookies (deleted when you close your browser) or "persistent" cookies (remain on your device for a set period).`,
  },
  {
    title: "2. How We Use Cookies",
    content: `Opero uses cookies for the following purposes:\n\n- Authentication: to keep you signed in and secure your session\n- Preferences: to remember your settings (theme, language, timezone)\n- Analytics: to understand how our Service is used and improve performance\n- Security: to detect and prevent fraudulent activity`,
  },
  {
    title: "3. Types of Cookies We Use",
    content: `**Essential Cookies** — Required for the Service to function. These cannot be disabled. They handle authentication, security tokens, and session management.\n\n**Functional Cookies** — Remember your preferences such as dark mode, language, and notification settings. Disabling these may affect your experience.\n\n**Analytics Cookies** — Help us understand usage patterns through anonymized, aggregated data. We use privacy-focused analytics that do not track individual users across sites.`,
  },
  {
    title: "4. Third-Party Cookies",
    content: `We minimize the use of third-party cookies. Our payment processor may set cookies during the checkout process. We do not use advertising cookies or allow third-party ad networks to set cookies on our Service.`,
  },
  {
    title: "5. Managing Cookies",
    content: `You can control cookies through your browser settings. Most browsers allow you to:\n\n- View and delete cookies\n- Block cookies from specific or all websites\n- Set preferences for first-party vs. third-party cookies\n- Enable "Do Not Track" signals\n\nPlease note that blocking essential cookies may prevent the Service from functioning properly.`,
  },
  {
    title: "6. Changes to This Policy",
    content: `We may update this Cookie Policy from time to time. Changes will be posted on this page with an updated effective date. Continued use of the Service after changes constitutes acceptance of the updated policy.`,
  },
  {
    title: "7. Contact Us",
    content: `If you have questions about our use of cookies, contact us at corporate@digiits.com.`,
  },
];

export function CookiePolicyPage() {
  return (
    <main className="pt-28 pb-20">
      <article className="mx-auto max-w-3xl px-6">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-medium text-slate-500 dark:text-slate-400 hover:text-primary-500 dark:hover:text-primary-400 transition-colors"
        >
          <ArrowLeft size={16} />
          Back to Home
        </Link>

        <h1 className="mt-8 text-3xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-4xl">
          Cookie Policy
        </h1>
        <p className="mt-3 text-sm text-slate-500 dark:text-slate-400">
          Effective date: {EFFECTIVE_DATE}
        </p>

        <div className="mt-6 border-b border-slate-200 dark:border-slate-800 pb-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
          This Cookie Policy explains how Opero uses cookies and similar
          technologies when you use our platform.
        </div>

        <div className="mt-8 space-y-10">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white">
                {section.title}
              </h2>
              <div className="mt-3 space-y-3">
                {section.content.split("\n\n").map((block, i) => {
                  const trimmed = block.trim();
                  if (trimmed.startsWith("- ")) {
                    const items = trimmed.split("\n").filter((l) => l.trim());
                    return (
                      <ul
                        key={i}
                        className="list-disc space-y-1.5 pl-6 text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                      >
                        {items.map((item, j) => (
                          <li key={j}>{item.replace(/^-\s*/, "")}</li>
                        ))}
                      </ul>
                    );
                  }
                  return (
                    <p
                      key={i}
                      className="text-sm leading-relaxed text-slate-600 dark:text-slate-300"
                    >
                      {renderFormattedText(trimmed)}
                    </p>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        <div className="mt-16 border-t border-slate-200 dark:border-slate-800 pt-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium text-primary-500 dark:text-primary-400 hover:underline"
          >
            <ArrowLeft size={16} />
            Back to Home
          </Link>
        </div>
      </article>
    </main>
  );
}
